/**
 * Admin-editable AI system prompts as tagged DocumentReferences.
 *
 * Prompt text lives in `content[0].attachment.data` (base64 UTF-8, text/plain).
 * Discovery uses DocumentReference.meta.tag (kind + subject).
 * Hosts (HealthcareService, Organization, Questionnaire) do not carry prompt extensions.
 */
import type { Coding, DocumentReference, Resource } from '@medplum/fhirtypes';

import {
  AI_PROMPT_BINARY_CONTENT_TYPE,
  AI_PROMPT_KIND_SYSTEM,
  AI_PROMPT_SUBJECT_SYSTEM,
  AI_PROMPT_TAG,
  AI_PROMPT_TAG_CODE,
  UNIFY_TAG_SYSTEM,
  type AiPromptKind,
} from './constants';
import { withUnifyProfile } from './profiles/with-unify-profile';

/** Minimal FHIR client surface used by the prompt store (MedplumClient-compatible). */
export interface AiPromptBinaryClient {
  searchResources(resourceType: 'DocumentReference', query: string): Promise<DocumentReference[]>;
  createResource<T extends Resource>(resource: T): Promise<T>;
  deleteResource(resourceType: 'DocumentReference', id: string): Promise<unknown>;
}

export function normalizeAiPromptSubjectRef(subjectRef: string): string {
  return subjectRef.trim().replace(/^\/+/, '');
}

export function buildAiPromptBinaryTags(kind: AiPromptKind, subjectRef: string): Coding[] {
  const subject = normalizeAiPromptSubjectRef(subjectRef);
  return [
    { ...AI_PROMPT_TAG },
    { system: AI_PROMPT_KIND_SYSTEM, code: kind, display: kind },
    { system: AI_PROMPT_SUBJECT_SYSTEM, code: subject, display: subject },
  ];
}

/** FHIR search query for DocumentReferences matching kind + subject (AND on tags). */
export function buildAiPromptSearchQuery(kind: AiPromptKind, subjectRef: string): string {
  const subject = normalizeAiPromptSubjectRef(subjectRef);
  const tags = [
    `${UNIFY_TAG_SYSTEM}|${AI_PROMPT_TAG_CODE}`,
    `${AI_PROMPT_KIND_SYSTEM}|${kind}`,
    `${AI_PROMPT_SUBJECT_SYSTEM}|${subject}`,
  ];
  return `${tags.map((tag) => `_tag=${encodeURIComponent(tag)}`).join('&')}&_count=20`;
}

function utf8BytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

function base64ToUtf8Bytes(base64: string): string {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

export function encodeAiPromptBinaryData(text: string): string {
  return utf8BytesToBase64(new TextEncoder().encode(text));
}

export function decodeAiPromptAttachmentData(data: string | undefined): string | undefined {
  const trimmed = data?.trim();
  if (!trimmed) return undefined;
  try {
    return base64ToUtf8Bytes(trimmed).trim() || undefined;
  } catch {
    return undefined;
  }
}

export function buildAiPromptDocumentReference(
  kind: AiPromptKind,
  subjectRef: string,
  text: string,
): DocumentReference {
  const subject = normalizeAiPromptSubjectRef(subjectRef);
  const tags = buildAiPromptBinaryTags(kind, subject);
  return withUnifyProfile({
    resourceType: 'DocumentReference',
    status: 'current',
    type: {
      coding: [{ ...AI_PROMPT_TAG }],
      text: 'AI Prompt',
    },
    description: `AI system prompt (${kind}) for ${subject}`,
    content: [
      {
        attachment: {
          contentType: AI_PROMPT_BINARY_CONTENT_TYPE,
          data: encodeAiPromptBinaryData(text),
          title: `${kind} prompt`,
        },
      },
    ],
    meta: { tag: tags },
  });
}

export async function searchAiPromptDocumentReferences(
  client: AiPromptBinaryClient,
  kind: AiPromptKind,
  subjectRef: string,
): Promise<DocumentReference[]> {
  return client.searchResources('DocumentReference', buildAiPromptSearchQuery(kind, subjectRef));
}

function readPromptTextFromDocumentReference(doc: DocumentReference): string | undefined {
  for (const entry of doc.content ?? []) {
    const text = decodeAiPromptAttachmentData(entry.attachment?.data);
    if (text) return text;
  }
  return undefined;
}

/** Returns the custom prompt text, or undefined when none is configured. */
export async function resolveAiPromptText(
  client: AiPromptBinaryClient,
  kind: AiPromptKind,
  subjectRef: string,
): Promise<string | undefined> {
  const matches = await searchAiPromptDocumentReferences(client, kind, subjectRef);
  for (const doc of matches) {
    const text = readPromptTextFromDocumentReference(doc);
    if (text) return text;
  }
  return undefined;
}

async function deleteAiPromptDocumentReferences(
  client: AiPromptBinaryClient,
  docs: DocumentReference[],
): Promise<void> {
  for (const doc of docs) {
    if (doc.id) {
      await client.deleteResource('DocumentReference', doc.id);
    }
  }
}

/** Deletes all matching prompt DocumentReferences for kind + subject. */
export async function clearAiPrompt(
  client: AiPromptBinaryClient,
  kind: AiPromptKind,
  subjectRef: string,
): Promise<void> {
  const existing = await searchAiPromptDocumentReferences(client, kind, subjectRef);
  await deleteAiPromptDocumentReferences(client, existing);
}

/**
 * Saves a custom prompt as a tagged DocumentReference with attachment.data,
 * replacing any prior matches. Blank / whitespace-only text clears the prompt.
 */
export async function saveAiPromptText(
  client: AiPromptBinaryClient,
  kind: AiPromptKind,
  subjectRef: string,
  text: string | undefined,
): Promise<DocumentReference | undefined> {
  const trimmed = text?.trim();
  const existing = await searchAiPromptDocumentReferences(client, kind, subjectRef);
  if (!trimmed) {
    await deleteAiPromptDocumentReferences(client, existing);
    return undefined;
  }
  const created = await client.createResource(
    buildAiPromptDocumentReference(kind, subjectRef, trimmed),
  );
  await deleteAiPromptDocumentReferences(client, existing);
  return created;
}

/** Test/fixture helper: DocumentReference with inline attachment.data + mock search key. */
export function buildAiPromptStoreFixtures(
  kind: AiPromptKind,
  subjectRef: string,
  text: string,
  ids?: { docId?: string },
): { document: DocumentReference; searchKey: string } {
  const docId = ids?.docId ?? `doc-${kind}`;
  const document: DocumentReference = {
    ...buildAiPromptDocumentReference(kind, subjectRef, text),
    id: docId,
  };
  return {
    document,
    searchKey: `DocumentReference?${buildAiPromptSearchQuery(kind, subjectRef)}`,
  };
}
